import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import {
  Layout,
  Avatar,
  Badge,
  Menu,
  Dropdown,
  FloatButton,
  Select
} from 'antd';

import useWindowDimensions from 'hooks/useWindowDimensions';

import { AppDispatch, RootState } from 'store';
import {
  getNotifications,
  getUnreadNotifications
} from 'store/slice/common';
import { actionLogout } from 'store/slice/auth';

import { MAIN_ROUTER } from 'routers/constants';
import webStorageClient from 'utils/webStorageClient';

import Notification from 'components/core/common/Notification';

import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  UserOutlined,
  LogoutOutlined,
  LockOutlined,
  BankOutlined
} from '@ant-design/icons';


import menuItem from 'data/sidebar';
import { canAccessRoute } from 'utils/permissions';

import common from 'data/common';

import { LayoutMain, Header, Logo, UserInfo, ContentWrapper } from './styled';
import { G14 } from 'helpers/data/user';

const { Sider, Content } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group'
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type
  } as MenuItem;
}

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const { t } = useTranslation('common');

  const dimensions: any = useWindowDimensions();

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const { isAuth } = useSelector((state: RootState) => state.auth);
  const { userInfo } = useSelector((state: RootState) => state.auth);


  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [userInfoText, setUserInfoText] = useState('');

  const handleToggleCollapse = () => {
    setCollapsed(prev => !prev);
    [50, 150, 250, 350].forEach(delay => {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, delay);
    });
  };

  useEffect(() => {
    if (isAuth) {
      if (userInfo?.ou?.length > 0) {
        if (userInfo?.ou === G14) {
          dispatch(getNotifications({ page: 1, pagesize: 10, count: true }));
          dispatch(
            getUnreadNotifications({ count: true, filter: `{'status':0}` })
          );
        } else {
          dispatch(
            getNotifications({
              page: 1,
              pagesize: 10,
              count: true,
              filter: `{'codeOrg':'${userInfo?.ou}'}`
            })
          );
          dispatch(
            getUnreadNotifications({
              count: true,
              filter: `{'status': 0, codeOrg:'${userInfo?.ou}' }`
            })
          );
        }
      }
    }

    if (dimensions?.width <= 767) {
      setCollapsed(true);
    }
  }, [dispatch, location, isAuth, dimensions?.width]);

  // TODO: Fetch user organization info from new backend if needed
  useEffect(() => {
    setUserInfoText('');
  }, [userInfo]);

  const handleActionLogout = useCallback(async () => {
    webStorageClient?.removeAll();
    localStorage.clear();
    await dispatch(actionLogout());
    window.location.replace('/auth/login');
  }, [dispatch]);



  const itemsMenu: MenuItem[] = menuItem
    ?.filter(item => item.childrens?.length
      ? item.childrens.some(child => !child.route || canAccessRoute(userInfo, child.route))
      : !item.route || canAccessRoute(userInfo, item.route))
    ?.map(item => {
      if ((item.childrens?.length ?? 0) > 0) {
        return getItem(
          <div className={item?.type === 'group' ? 'menu-group-title' : ''}>{!collapsed && t(item?.key)}</div>,
          item?.key,
          item?.icon ? <item.icon /> : null,
          item?.childrens
            ?.filter(el => !el.route || canAccessRoute(userInfo, el.route))
            ?.map((itemChild: any) => {
              if (itemChild?.route) {
                return getItem(
                  <p
                    onClick={() => {
                      itemChild?.route && navigate(itemChild.route);
                    }}
                  >
                    {t(itemChild.key)}
                  </p>,
                  itemChild.key,
                  itemChild?.icon ? (
                    <itemChild.icon
                      onClick={() => {
                        itemChild?.route && navigate(itemChild.route);
                      }}
                    />
                  ) : null
                );
              } else {
                return getItem(
                  <a href={itemChild?.url} target="_blank" rel="noreferrer">
                    {t(itemChild.key)}
                  </a>,
                  itemChild.key,
                  itemChild?.icon ? <itemChild.icon /> : null
                );
              }
            }),
          item?.type
        );
      }
      return getItem(
        <p
          onClick={() => {
            item.route && navigate(item.route);
          }}
        >
          {t(item.key)}
        </p>,
        item.key,
        item?.icon ? (
          <item.icon
            onClick={() => {
              item?.route && navigate(item.route);
            }}
          />
        ) : null,
        undefined,
        item?.type
      );
    });




  const accountItems: MenuProps['items'] = useMemo(() => {
    return [
      {
        key: 'profile',
        label: (
          <UserInfo
            onClick={() => {
              navigate(MAIN_ROUTER.PROFILE);
            }}
          >
            <div className="user-unit">
              <UserOutlined /> <span>{userInfo.mail}</span>
            </div>
            <div className="user-unit">
              {userInfo.ou ? <BankOutlined /> : null}
              <span>
                {userInfo.ou ? `${userInfoText} (${userInfo?.ou})` : ''}
              </span>
            </div>
          </UserInfo>
        )
      },
      {
        key: 'change-password',
        label: (
          <p onClick={() => navigate(MAIN_ROUTER.CHANGE_PASSWORD)}>
            <LockOutlined /> {t('changePassword')}
          </p>
        )
      },
      {
        key: 'logout',
        label: (
          <p onClick={() => handleActionLogout()}>
            <LogoutOutlined /> {t('logout')}
          </p>
        )
      }
    ];
  }, [handleActionLogout, navigate, t, userInfoText, userInfo]);





  useEffect(() => {
    if (window.screen.width < 768) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, []);



  const findPath = useMemo(() => {
    const path = window.location?.pathname;

    let find = { key: '', expand: '' };

    menuItem?.forEach(item => {
      if (item?.childrens && item?.childrens?.length > 0) {
        item?.childrens?.forEach(elm => {
          if (
            elm?.route &&
            (elm?.route === path || path?.includes(elm?.route))
          ) {
            find.key = elm?.key;
            find.expand = item?.key;
          }
        });
      }

      if (item?.route && item?.route === path) {
        find.key = item?.key;
      }
    });
    return find || '';
  }, []);

  const isDashboardHomePath = useMemo(() => {
    const path = location.pathname.replace(/\/$/, '') || '/';
    return [
      MAIN_ROUTER.HOME,
      MAIN_ROUTER.DASHBOARD_HOME,
      MAIN_ROUTER.DASHBOARD_DEVICE,
      MAIN_ROUTER.DASHBOARD_BODY,
      MAIN_ROUTER.DASHBOARD_VIBRATION,
      MAIN_ROUTER.DASHBOARD_LASER,
      MAIN_ROUTER.DASHBOARD_MAINTENANCE,
      MAIN_ROUTER.DASHBOARD_INCIDENT,
      MAIN_ROUTER.DASHBOARD_ERROR_LOG_LIST,
      MAIN_ROUTER.DASHBOARD_INCIDENT_LIST,
      MAIN_ROUTER.DASHBOARD_MATERIAL,
      MAIN_ROUTER.DASHBOARD_FEATURE,
      MAIN_ROUTER.DASHBOARD_ANALYTICS,
      MAIN_ROUTER.SYSTEM_USER,
      MAIN_ROUTER.SYSTEM_ROLE
    ].map(p => p.replace(/\/$/, '') || '/').includes(path);
  }, [location.pathname]);

  return (
    <LayoutMain $isCollapse={collapsed}>
      <Sider width={260} trigger={null} collapsible collapsed={collapsed} theme="light">
        <div className="logo">
          {!collapsed && <Logo $isSmall={collapsed} src={common?.logo} alt="logo" />}
          <div
            className="collapse-custom"
            onClick={handleToggleCollapse}
          >
            {collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
          </div>
        </div>
        <div className="menu-sidebar">
          <Menu
            mode="inline"
            theme="light"
            defaultSelectedKeys={[findPath.key || 'dashboardHome']}
            defaultOpenKeys={[findPath.expand || '']}
            items={itemsMenu}
          />
        </div>
        <div className="logout">
          {!collapsed ? (
            <button
              onClick={() => {
                handleActionLogout();
              }}
            >
              <LogoutOutlined /> {t('logout')}
            </button>
          ) : (
            <button
              onClick={() => {
                handleActionLogout();
              }}
            >
              <LogoutOutlined />
            </button>
          )}
        </div>
      </Sider>
      <Layout className="site-layout">
        <Header $isCollapse={collapsed}>
          <div className="left">
            <div className="switch-module" style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <h2 style={{ margin: 0, fontWeight: 600, fontSize: '18px', textTransform: 'uppercase' }}>HỆ THỐNG GIÁM SÁT QUẢN LÝ THIẾT BỊ</h2>
            </div>
          </div>
          <div className="right">
            <Select 
              defaultValue="codien" 
              style={{ width: 180, marginRight: 16 }} 
              options={[{ value: 'codien', label: 'CÔNG TY CƠ ĐIỆN' }]} 
            />
            <Notification />
            <Dropdown
              menu={{ items: accountItems }}
              placement="bottom"
              trigger={['click']}
              overlayClassName="dropdown-user"
            >
              <Badge dot>
                <Avatar shape="square" icon={<UserOutlined />} />
              </Badge>
            </Dropdown>
          </div>
        </Header>
        <Content>
          {isDashboardHomePath ? (
            children
          ) : (
            <ContentWrapper>{children}</ContentWrapper>
          )}
          <FloatButton.BackTop />
        </Content>
      </Layout>
    </LayoutMain>
  );
};

export default MainLayout;
